import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const Achievements = () => {
  const achievements = [
    {
      icon: "TrendingUp",
      title: "Market Leadership",
      value: "40%",
      description: "Market share in endoscopic equipment distribution across Malaysia",
      color: "primary"
    },
    {
      icon: "Users",
      title: "Healthcare Partners",
      value: "500+",
      description: "Hospitals, clinics, and medical centers trust our solutions",
      color: "success"
    },
    {
      icon: "Award",
      title: "Excellence Awards",
      value: "12",
      description: "Industry recognition for innovation and service quality",
      color: "accent"
    },
    {
      icon: "Globe",
      title: "Global Partnerships",
      value: "15+",
      description: "Strategic alliances with leading international manufacturers",
      color: "primary"
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: "Dr. Siti Aminah",
      position: "Chief of Gastroenterology",
      hospital: "Kuala Lumpur General Hospital",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      quote: `Endosurgery has been our trusted partner for over 8 years. Their commitment to quality and exceptional service has significantly enhanced our endoscopic procedures and patient outcomes.`,
      rating: 5
    },
    {
      id: 2,
      name: "Prof. Dr. Lim Chee Kong",
      position: "Head of Surgery Department",
      hospital: "Penang Medical Centre",
      image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      quote: `The technical expertise and continuous support from Endosurgery team have been invaluable. They understand our clinical needs and consistently deliver solutions that exceed expectations.`,
      rating: 5
    },
    {
      id: 3,
      name: "Dr. Raj Patel",
      position: "Senior Consultant Urologist",
      hospital: "Subang Jaya Medical Centre",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      quote: `Their GDPMD certification and quality assurance processes give us complete confidence in the products we use. Endosurgery sets the gold standard for medical device distribution in Malaysia.`,
      rating: 5
    }
  ];

  const certifications = [
    {
      name: "GDPMD Certified",
      description: "Good Distribution Practice for Medical Devices",
      icon: "Shield",
      color: "success"
    },
    {
      name: "ISO 13485",
      description: "Quality Management Systems for Medical Devices",
      icon: "Award",
      color: "primary"
    },
    {
      name: "CE Marking",
      description: "European Conformity for Medical Products",
      icon: "CheckCircle",
      color: "accent"
    },
    {
      name: "FDA Approved",
      description: "US Food and Drug Administration Compliance",
      icon: "Verified",
      color: "success"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Trophy" size={16} color="var(--color-primary)" />
            <span className="text-primary font-semibold text-sm">Our Success</span>
          </div>
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-text-primary mb-6">
            Achievements & Recognition
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Over 12 years of excellence in medical technology distribution, 
            recognized by healthcare professionals and industry leaders across Malaysia.
          </p>
        </motion.div>

        {/* Achievement Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {achievements?.map((achievement, index) => (
            <motion.div
              key={achievement?.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="text-center"
            >
              <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-medical hover:shadow-medical-hover transition-all duration-300 group">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 ${
                  achievement?.color === 'primary' ? 'bg-primary/10' :
                  achievement?.color === 'success' ? 'bg-success/10' :
                  achievement?.color === 'accent' ? 'bg-accent/10' :
                  achievement?.color === 'warning' ? 'bg-warning/10' : 'bg-primary/10'
                }`}>
                  <Icon 
                    name={achievement?.icon} 
                    size={32} 
                    className={`${
                      achievement?.color === 'primary' ? 'text-primary' :
                      achievement?.color === 'success' ? 'text-success' :
                      achievement?.color === 'accent' ? 'text-accent' :
                      achievement?.color === 'warning' ? 'text-warning' : 'text-primary'
                    }`}
                    strokeWidth={2} 
                  />
                </div>
                <div className={`text-4xl lg:text-5xl font-bold mb-3 ${
                  achievement?.color === 'primary' ? 'text-primary' :
                  achievement?.color === 'success' ? 'text-success' :
                  achievement?.color === 'accent' ? 'text-accent' :
                  achievement?.color === 'warning' ? 'text-warning' : 'text-primary'
                }`}>
                  {achievement?.value}
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {achievement?.title}
                </h3>
                <p className="text-sm text-text-secondary leading-relaxed">
                  {achievement?.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Client Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              What Healthcare Professionals Say
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Trusted by leading medical professionals across Malaysia's top healthcare institutions.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) => (
              <motion.div
                key={testimonial?.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-medical hover:shadow-medical-hover transition-all duration-300"
              >
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-warning fill-warning" />
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-text-secondary leading-relaxed mb-6 italic">
                  "{testimonial?.quote}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-primary/20">
                    <Image
                      src={testimonial?.image}
                      alt={testimonial?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="font-semibold text-text-primary">{testimonial?.name}</div>
                    <div className="text-sm text-text-secondary">{testimonial?.position}</div>
                    <div className="text-xs text-primary font-medium">{testimonial?.hospital}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Certifications & Compliance
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Our commitment to quality is validated by international certifications and regulatory compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications?.map((cert, index) => (
              <motion.div
                key={cert?.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-card border border-border rounded-lg p-6 text-center shadow-medical hover:shadow-medical-hover transition-all duration-300 group"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 group-hover:scale-110 transition-transform duration-300 ${
                  cert?.color === 'primary' ? 'bg-primary/10' :
                  cert?.color === 'success' ? 'bg-success/10' :
                  cert?.color === 'accent' ? 'bg-accent/10' :
                  cert?.color === 'warning' ? 'bg-warning/10' : 'bg-primary/10'
                }`}>
                  <Icon 
                    name={cert?.icon} 
                    size={24} 
                    className={`${
                      cert?.color === 'primary' ? 'text-primary' :
                      cert?.color === 'success' ? 'text-success' :
                      cert?.color === 'accent' ? 'text-accent' :
                      cert?.color === 'warning' ? 'text-warning' : 'text-primary'
                    }`}
                  />
                </div>
                <h4 className="font-semibold text-text-primary mb-2">{cert?.name}</h4>
                <p className="text-sm text-text-secondary">{cert?.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 lg:mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8 lg:p-12 border border-primary/20">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Join Our Success Story
            </h3>
            <p className="text-text-secondary mb-8 max-w-2xl mx-auto">
              Partner with Malaysia's leading medical technology distributor and experience 
              the difference that quality, expertise, and dedication can make.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200"
              >
                <Icon name="MessageCircle" size={20} className="mr-2" />
                Start Partnership
              </Link>
              <Link
                to="/product-portfolio"
                className="inline-flex items-center justify-center px-8 py-3 bg-transparent border border-primary text-primary font-semibold rounded-lg hover:bg-primary/5 transition-colors duration-200"
              >
                <Icon name="Package" size={20} className="mr-2" />
                View Products
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Achievements;